/**
 * Stale-deployment recovery for lazy-loaded route chunks.
 *
 * When a route chunk fails to load (e.g. old chunk hashes no longer exist
 * after a new release), reload once to pick up the new bundle rather than
 * showing an unhandled error to the user. A sessionStorage key per route
 * prevents infinite reload loops; the second failure lands on notFound.
 *
 * Registered via router.onError() in ./index.ts. Built as a factory taking
 * the router instance to avoid a circular import back into ./index.ts.
 */

import type { RouteLocationNormalized, Router } from 'vue-router';

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  return (
    error.message.includes("Couldn't resolve component") ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Unable to preload CSS for') ||
    error.name === 'ChunkLoadError'
  );
};

export const createChunkReloadHandler =
  (router: Router) =>
  (error: unknown, to: RouteLocationNormalized): void => {
    if (!isChunkLoadError(error)) return;

    const reloadKey = `chunk-reload:${to.fullPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      // We use hash-based history, so `to.fullPath` is the route WITHOUT the
      // leading '#'. Passing it to location.assign() would write it into the
      // document pathname (e.g. /depot/...), breaking hash routing and the
      // Google Maps referrer allowlist. Set the hash explicitly, then reload
      // to pick up the fresh bundle.
      window.location.hash = to.fullPath;
      window.location.reload();
    } else {
      // Already reloaded once — avoid infinite loop, navigate to error page
      router.push({ name: 'notFound' });
    }
  };
